import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useAppDispatch } from '../../app/hooks';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import TabBox from '../organisms/TabBox';
import Offset from '../organisms/Offset';
import EditForm from '../organisms/EditForm';
import VenueLocationForm from './VenueLocationForm';
import Map from '../molecules/MapBox';
import Property from '../atoms/Property';
import Empty from '../atoms/Empty';
import Input from '../atoms/Input';
import Button from '../atoms/Button';
import { display } from '../../data/display';
import { updateVenueState } from '../../features/venue/venueSlice';
import { updateVenue } from '../../lib/apis/venue';
import styles from '../../styles/components/organisms/VenueLocations.module.scss';
import router from 'next/router';

interface VenueLocationsProps {
  venueID: string;
  data: any;
  access?: boolean;
  setLayer: (el: any) => void;
  apply: (id: string, data?: any) => void;
}

const VenueLocations = (props: VenueLocationsProps) => {
  const { t } = useTranslation(['common', 'venue']);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState() as any;
  const [fieldErrors, setFieldErrors] = useState([]) as any;
  const [edit, setEdit] = useState(false);
  const [tab, setTab] = useState('ground');
  const { venueID, data, access, setLayer, apply } = props;
  const { venueLocations } = styles;
  const planData = display.venue.platinum as any;

  /*
   * Locations
   */

  // Get fields to display
  const displayObj = planData.locations;

  // Tabs
  const tabKeys = Object.keys(displayObj);
  const tabs = tabKeys.map((key) => ({
    id: key,
    label: t(`venue:locations:${key}`),
  }));

  // Properties by tab
  const propertiesByTab = displayObj[tab] || {};
  const propertyKeys = Object.keys(propertiesByTab).filter(
    (key: string) => key !== 'ground'
  );

  const properties = propertyKeys.map((key: string) => {
    const id = key; // as keyof VenueLocationProperties;
    const defaults = propertiesByTab[id];
    const value = data[id] || (defaults ? defaults.value : '');
    const label = t(`venue:locations:description`);
    return { ...defaults, id, value, label };
  });

  const { location } = data || {};
  const { lat, lng } = location || {};

  /**
   * Handle form submit
   */
  const submit = async (formData: any) => {
    setError(false);
    setLoading(true);
    try {
      const venue = await updateVenue(venueID, formData);
      dispatch(updateVenueState(venue));
      const url = new URL(window.location.href);
      url.pathname = `/venue/${venue.country}/${venue.city}/${venue.slug}`;
      router.replace(url);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
      setEdit(false);
    }
  };

  const onTabChange = (id: string) => {
    if (id !== tab) {
      setTab(id);
      setEdit(false);
    }
  };

  return (
    <Row className={venueLocations}>
      <Col xs={0} md={0} className="d-none d-md-block">
        <TabBox
          onChange={onTabChange}
          title={t('venue:locations:headline')}
          tabs={tabs}
        >
          {tab === 'ground' ? (
            <>
              <br />
              <Map type="venue" large data={{ lat, lng }} />
            </>
          ) : (
            <Offset>
              {!properties.length ? (
                <Empty />
              ) : edit ? (
                <EditForm
                  noCols
                  float
                  id={'locationsForm'}
                  error={error}
                  loading={loading}
                  onSubmit={submit}
                  onCancel={() => setEdit(false)}
                >
                  {properties.map((el: any, i: number) => (
                    <Input
                      key={i}
                      float
                      medium
                      reset
                      optional
                      full={properties.length < 4}
                      name={el.id}
                      icon={el.id}
                      label={el.label}
                      defaultValue={el.value}
                      type={el.type}
                      format={el.format}
                      unit={el.unit}
                      placeholder={el.help}
                      // onChange={(obj) => {addData("offerProps", obj)}}
                    />
                  ))}
                </EditForm>
              ) : (
                properties.map((el: any, i: number) => (
                  <Property
                    key={i}
                    float
                    medium
                    full={properties.length < 4}
                    icon={tab}
                    label={el.label}
                    value={el.value}
                    unit={el.unit}
                  />
                ))
              )}
            </Offset>
          )}
        </TabBox>

        {!!access && !edit && (
          <Button
            small
            dark
            icon={'edit'}
            label={t(`common:cta-edit`)}
            onClick={
              tab === 'ground'
                ? () =>
                    setLayer(
                      <VenueLocationForm
                        prefill={location}
                        venueID={venueID}
                        onSubmit={() => setLayer(null)}
                        onCancel={() => setLayer(null)}
                      />
                    )
                : () => setEdit(true)
            }
          />
        )}

        <br />
        <br />
        <br />
        <br />
      </Col>
    </Row>
  );
};

export default VenueLocations;
